import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  Space,
  Button,
  Tag,
  Badge,
  message,
  Modal,
  Input,
  Form,
  Select,
  Spin,
  Tabs
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MailOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosConfig from '../../../../../config/axiosConfig';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const PendingCustomersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingPartnerRequests, setPendingPartnerRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('customers');
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [rejectForm] = Form.useForm();

  // Mock data - replace with API call
  const mockPendingApprovals = [
    {
      id: 1,
      customerName: 'TechCorp Solutions',
      businessType: 'Technology',
      requestDate: '2025-09-10',
      planType: 'Enterprise',
      status: 'verification_pending',
      documents: ['business_license', 'tax_certificate'],
      priority: 'high',
      email: 'contact@techcorp.com',
      phone: '+1-234-567-8900'
    },
    {
      id: 2,
      customerName: 'Digital Marketing Pro',
      businessType: 'Marketing',
      requestDate: '2025-09-09',
      planType: 'Premium',
      status: 'document_review',
      documents: ['business_license'],
      priority: 'medium',
      email: 'info@digitalmarketing.com',
      phone: '+1-234-567-8901'
    },
    {
      id: 3,
      customerName: 'E-commerce Hub',
      businessType: 'Retail',
      requestDate: '2025-09-08',
      planType: 'Business',
      status: 'compliance_check',
      documents: ['business_license', 'tax_certificate', 'compliance_docs'],
      priority: 'high',
      email: 'admin@ecommercehub.com',
      phone: '+1-234-567-8902'
    },
    {
      id: 4,
      customerName: 'Healthcare Connect',
      businessType: 'Healthcare',
      requestDate: '2025-09-07',
      planType: 'Enterprise',
      status: 'final_approval',
      documents: ['business_license', 'healthcare_license'],
      priority: 'critical',
      email: 'support@healthcareconnect.com',
      phone: '+1-234-567-8903'
    }
  ];

  useEffect(() => {
    fetchPendingPartnerRequests();
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    setLoading(true);
    try {
      const requestBody = {
        companystatus: 'pending',
        page: 1,
        limit: 100
      };

      const response = await axiosConfig.post('/company/getcompanydetails', requestBody);
      
      if (response.data?.status === true && response.data?.statuscode === 200) {
        const companies = response.data.entity.companies || [];
        // Transform API data to match component expectations
        const transformedApprovals = companies.map((company, index) => ({
          id: company.companyid,
          customerName: company.companyname,
          businessType: company.company_type === 'SOLUTION_PARTNER' ? 'Solution Partner' : 'Direct Customer',
          requestDate: company.entrytime,
          planType: company.company_type === 'SOLUTION_PARTNER' ? 'Partner' : 'Business',
          status: company.isverified === 1 ? 'final_approval' : 'verification_pending',
          documents: ['business_license', 'tax_certificate'],
          priority: company.isverified === 1 ? 'low' : 'high',
          email: company.emailid,
          phone: `${company.countrycode || ''}${company.contactno || ''}`
        }));
        setPendingApprovals(transformedApprovals);
      } else {
        // Fallback to mock data if API fails
        setPendingApprovals(mockPendingApprovals);
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      message.error('Failed to load pending approvals');
      // Fallback to mock data
      setPendingApprovals(mockPendingApprovals);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingPartnerRequests = async () => {
    try {
      const response = await axiosConfig.get('/dashboard/partner-request');
      
      if (response.data?.status === true && response.data?.statuscode === 200) {
        // Filter only pending requests and transform the data
        const pendingRequests = response.data.entity?.requests?.filter(request => 
          request.status === 0
        ) || [];
        
        // Transform partner request data to match table structure
        const transformedData = pendingRequests.map(request => ({
          id: request.customeruuid,
          customerName: request.partner_name,
          businessType: 'Partner Request',
          requestDate: request.created_at,
          planType: 'Solution Partner',
          status: 'verification_pending',
          documents: ['partner_application'],
          priority: 'high',
          email: request.support_email,
          phone: 'N/A',
          // Additional partner-specific fields
          slug: request.slug,
          partner_logo: request.partner_logo,
          primary_color: request.primary_color,
          branding_config: request.branding_config,
          business_config: request.business_config,
          customeruuid: request.customeruuid,
          isPartnerRequest: true
        }));
        
        setPendingPartnerRequests(transformedData);
      } else {
        setPendingPartnerRequests([]);
      }
    } catch (error) {
      console.error('Error fetching pending partner requests:', error);
      setPendingPartnerRequests([]);
    }
  };

  const handleApproveCustomer = async (record) => {
    try {
      setLoading(true);
      
      // API call to approve customer using the specified endpoint
      const payload = {
        companyuuid: record.id || record.customeruuid,
        status: 1, // 1 means accept
        approveby: "Message OS" // Name of the person who is accepting the request
      };
      
      const response = await axiosConfig.post('/dashboard/partner-status-update', payload);
      
      if (response.data?.status === true || response.data?.success) {
        message.success(`Customer ${record.customerName} approved successfully!`);
        // Refresh the data
        if (record.isPartnerRequest) {
          fetchPendingPartnerRequests();
        } else {
          fetchPendingApprovals();
        }
      } else {
        message.error(response.data?.message || 'Failed to approve customer');
      }
    } catch (error) {
      console.error('Error approving customer:', error);
      message.error('Failed to approve customer');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectCustomer = async (values) => {
    try {
      setLoading(true);
      
      // API call to reject customer using the specified endpoint
      const payload = {
        companyuuid: selectedCustomer.id || selectedCustomer.customeruuid,
        status: 2, // 2 means reject
        approveby: "Message OS", // Name of the person who is rejecting the request
        rejection_reason: `${values.reason}: ${values.comments}` // Include both reason and comments
      };
      
      const response = await axiosConfig.post('/dashboard/partner-status-update', payload);
      
      if (response.data?.status === true || response.data?.success) {
        message.success(`Customer ${selectedCustomer.customerName} request rejected`);
        // Refresh the data
        if (selectedCustomer.isPartnerRequest) {
          fetchPendingPartnerRequests();
        } else {
          fetchPendingApprovals();
        }
        setRejectModalVisible(false);
        setSelectedCustomer(null);
        rejectForm.resetFields();
      } else {
        message.error(response.data?.message || 'Failed to reject customer');
      }
    } catch (error) {
      console.error('Error rejecting customer:', error);
      message.error('Failed to reject customer');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMoreInfo = async (customerId, requiredDocs) => {
    try {
      setLoading(true);
      // API call to request more information
      // await axiosConfig.post(`/admin/request-info/${customerId}`, { requiredDocs });
      
      message.success('Information request sent to customer');
    } catch (error) {
      message.error('Failed to send information request');
    } finally {
      setLoading(false);
    }
  };

  const showRejectModal = (customer) => {
    setSelectedCustomer(customer);
    setRejectModalVisible(true);
  };

  // Partner request handling functions - now using unified approach with handleApproveCustomer and handleRejectCustomer

  // Handle partner info request
  const handlePartnerInfoRequest = async (customeruuid, partnerName) => {
    try {
      setLoading(true);
      // API call to request more information from partner
      // await axiosConfig.post(`/dashboard/partner-request/${customeruuid}/request-info`, { requiredInfo });
      
      message.success(`Information request sent to ${partnerName}`);
    } catch (error) {
      message.error('Failed to send information request');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Sr No',
      key: 'srNo',
      width: 80,
      render: (_, record, index) => index + 1
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (name, record) => (
        <Space direction="vertical" size="small">
          <Text strong style={{ fontSize: '14px' }}>{name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.businessType} • {record.planType}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.email}
          </Text>
        </Space>
      )
    },
    {
      title: 'Request Date',
      dataIndex: 'requestDate',
      key: 'requestDate',
      render: (date) => moment(date).format('MMM DD, YYYY'),
      sorter: (a, b) => moment(a.requestDate).unix() - moment(b.requestDate).unix()
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          verification_pending: { color: 'orange', text: 'Verification Pending' },
          document_review: { color: 'blue', text: 'Document Review' },
          compliance_check: { color: 'purple', text: 'Compliance Check' },
          final_approval: { color: 'green', text: 'Final Approval' }
        };
        return <Tag color={statusConfig[status]?.color}>{statusConfig[status]?.text}</Tag>;
      },
      filters: [
        { text: 'Verification Pending', value: 'verification_pending' },
        { text: 'Document Review', value: 'document_review' },
        { text: 'Compliance Check', value: 'compliance_check' },
        { text: 'Final Approval', value: 'final_approval' }
      ],
      onFilter: (value, record) => record.status === value
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const priorityConfig = {
          critical: { color: 'red', icon: <ExclamationCircleOutlined /> },
          high: { color: 'orange', icon: <ArrowUpOutlined /> },
          medium: { color: 'blue', icon: <ArrowUpOutlined /> },
          low: { color: 'green', icon: <ArrowDownOutlined /> }
        };
        return (
          <Tag color={priorityConfig[priority]?.color}>
            {priorityConfig[priority]?.icon} {priority.toUpperCase()}
          </Tag>
        );
      },
      sorter: (a, b) => {
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
    },
    {
      title: 'Documents',
      dataIndex: 'documents',
      key: 'documents',
      render: (docs) => (
        <Space direction="vertical" size="small">
          {docs.map(doc => (
            <Tag key={doc} size="small" color="blue">
              {doc.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '200px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckCircleOutlined />}
              onClick={() => handleApproveCustomer(record)}
              style={{ 
                flex: 1,
                background: '#52c41a',
                borderColor: '#52c41a',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(82, 196, 26, 0.2)'
              }}
            >
              Approve
            </Button>
            <Button 
              danger 
              size="small" 
              icon={<CloseCircleOutlined />}
              onClick={() => showRejectModal(record)}
              style={{ 
                flex: 1,
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(255, 77, 79, 0.2)'
              }}
            >
              Reject
            </Button>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Button 
              size="small" 
              icon={<MailOutlined />}
              onClick={() => handleRequestMoreInfo(record.id)}
              style={{ 
                flex: 1,
                borderColor: '#1890ff',
                color: '#1890ff',
                fontWeight: 500
              }}
            >
              Request Info
            </Button>
            <Button 
              size="small" 
              icon={<EyeOutlined />}
              style={{ 
                flex: 1,
                borderColor: '#722ed1',
                color: '#722ed1',
                fontWeight: 500
              }}
            >
              Details
            </Button>
          </div>
        </div>
      )
    }
  ];

  // Columns for partner requests
  const partnerColumns = [
    {
      title: 'Sr No',
      key: 'srNo',
      width: 80,
      render: (_, record, index) => index + 1
    },
    {
      title: 'Partner Details',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (name, record) => (
        <Space direction="vertical" size="small">
          <Text strong style={{ fontSize: '14px' }}>{name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.businessType} • {record.planType}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.email}
          </Text>
          {record.slug && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Slug: {record.slug}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Request Date',
      dataIndex: 'requestDate',
      key: 'requestDate',
      render: (date) => moment(date).format('MMM DD, YYYY'),
      sorter: (a, b) => moment(a.requestDate).unix() - moment(b.requestDate).unix()
    },
    {
      title: 'Configuration',
      key: 'configuration',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text style={{ fontSize: '12px' }}>Color:</Text>
            <span style={{ 
              display: 'inline-block', 
              width: '16px', 
              height: '16px', 
              backgroundColor: record.primary_color,
              border: '1px solid #ccc',
              borderRadius: '2px'
            }}></span>
            <Text style={{ fontSize: '11px', color: '#666' }}>{record.primary_color}</Text>
          </div>
          {record.business_config && (
            <Text style={{ fontSize: '11px', color: '#666' }}>
              SMTP: {record.business_config.smtp_host}:{record.business_config.port}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: () => <Tag color="orange">Pending Review</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '200px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckCircleOutlined />}
              onClick={() => handleApproveCustomer(record)}
              style={{ 
                flex: 1,
                background: '#52c41a',
                borderColor: '#52c41a',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(82, 196, 26, 0.2)'
              }}
            >
              Approve
            </Button>
            <Button 
              danger 
              size="small" 
              icon={<CloseCircleOutlined />}
              onClick={() => {
                // Set the selected customer and show the reject modal for consistent handling
                setSelectedCustomer(record);
                setRejectModalVisible(true);
              }}
              style={{ 
                flex: 1,
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(255, 77, 79, 0.2)'
              }}
            >
              Reject
            </Button>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Button 
              size="small" 
              icon={<MailOutlined />}
              onClick={() => handlePartnerInfoRequest(record.customeruuid, record.customerName)}
              style={{ 
                flex: 1,
                borderColor: '#1890ff',
                color: '#1890ff',
                fontWeight: 500
              }}
            >
              Request Info
            </Button>
            <Button 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => {
                Modal.info({
                  title: 'Partner Request Details',
                  width: 600,
                  content: (
                    <div>
                      <p><strong>Company Name:</strong> {record.customerName}</p>
                      <p><strong>Support Email:</strong> {record.email}</p>
                      <p><strong>Slug:</strong> {record.slug}</p>
                      <p><strong>Primary Color:</strong> 
                        <span style={{ 
                          display: 'inline-block', 
                          width: '20px', 
                          height: '20px', 
                          backgroundColor: record.primary_color,
                          marginLeft: '8px',
                          border: '1px solid #ccc'
                        }}></span>
                        {record.primary_color}
                      </p>
                      <p><strong>SMTP Host:</strong> {record.business_config?.smtp_host || 'N/A'}</p>
                      <p><strong>SMTP Port:</strong> {record.business_config?.port || 'N/A'}</p>
                      <p><strong>SSL Enabled:</strong> {record.business_config?.ssl_enabled ? 'Yes' : 'No'}</p>
                      <p><strong>Created:</strong> {moment(record.requestDate).format('MMM DD, YYYY HH:mm')}</p>
                    </div>
                  )
                });
              }}
              style={{ 
                flex: 1,
                borderColor: '#722ed1',
                color: '#722ed1',
                fontWeight: 500
              }}
            >
              View Details
            </Button>
          </div>
        </div>
      )
    }
  ];

  return (
    <div >
      <Spin spinning={loading}>
        {/* Header */}
        <Card 
          bordered={false} 
          bodyStyle={{ padding: '20px 24px' }}
          style={{ marginBottom: 24 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/dashboard/admin')}
                type="default"
                size="large"
                style={{ 
                  fontWeight: 500
                }}
              >
                Back 
              </Button>
              <div>
                <Title level={2} style={{ margin: 0, fontWeight: 600 }}>
                  Partner Requests Management
                </Title>
                <Text style={{ color: '#666', fontSize: '14px' }}>
                  Review and manage pending partner requests
                </Text>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Badge 
                count={pendingPartnerRequests.length} 
                style={{ 
                  backgroundColor: '#ff4d4f',
                  boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)'
                }} 
                showZero
              />
              <Button 
                type="default" 
                icon={<ReloadOutlined />} 
                onClick={fetchPendingPartnerRequests}
                size="large"
                style={{
                  fontWeight: 500,
                   borderColor: '#52c41a',
                  color: '#52c41a'
                }}
              >
              </Button>
              <Button 
                type="default" 
                onClick={() => navigate('/dashboard/admin/active-customers')}
                size="large"
                style={{
                  fontWeight: 500,
                  borderColor: '#52c41a',
                  color: '#52c41a'
                }}
              >
                View Active
              </Button>
            </div>
          </div>
        </Card>

        {/* Partner Requests Table */}
        <Card 
          bordered={false}
          bodyStyle={{ padding: '20px' }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: '16px' }}>Pending Partner Requests</Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                {pendingPartnerRequests.length} requests awaiting review
              </Text>
            </div>
          }
        >
          <Table
            dataSource={pendingPartnerRequests}
            columns={partnerColumns}
            rowKey="id"
            size="small"
            scroll={{ x: 1400 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} partner requests`
            }}
          />
        </Card>

        {/* Reject Modal */}
        <Modal
          title="Reject Customer Request"
          open={rejectModalVisible}
          onCancel={() => {
            setRejectModalVisible(false);
            setSelectedCustomer(null);
            rejectForm.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form
            form={rejectForm}
            layout="vertical"
            onFinish={handleRejectCustomer}
          >
            <Form.Item
              name="reason"
              label="Rejection Reason"
              rules={[{ required: true, message: 'Please select a reason' }]}
            >
              <Select placeholder="Select rejection reason">
                <Option value="incomplete_documents">Incomplete Documents</Option>
                <Option value="invalid_business">Invalid Business Information</Option>
                <Option value="compliance_issues">Compliance Issues</Option>
                <Option value="duplicate_account">Duplicate Account</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="comments"
              label="Additional Comments"
              rules={[{ required: true, message: 'Please provide additional comments' }]}
            >
              <TextArea 
                rows={4} 
                placeholder="Provide detailed explanation for rejection..."
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" danger>
                  Reject Customer
                </Button>
                <Button onClick={() => {
                  setRejectModalVisible(false);
                  setSelectedCustomer(null);
                  rejectForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Spin>

      <style jsx>{`
        .critical-row {
          background-color: #fff2f0 !important;
        }
        .high-priority-row {
          background-color: #fff7e6 !important;
        }
      `}</style>
    </div>
  );
};

export default PendingCustomersPage;
